import type {
  QuestionNode,
  LATNode,
  TextToApplyNode,
  ConditionEdge,
  ConditionValue,
  SchedulerNode,
  AnswerId,
  Answer,
} from 'src/types/chatbot';
import type {Validation} from 'src/utils/validation';
import type {ConversationDispatch} from './reducer';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useSmsContext} from './sms-context';
import classify from 'src/utils/classify';
import {keyBy} from 'src/utils/map';
import {formatCompactNumber} from 'src/utils/number';
import {
  getMultipleChoiceLabel,
  getConditionOperatorLabels,
} from 'src/components/conversation-builder/utils';

import {UnstyledButton} from 'src/components/lib/new-button';

import DeleteIcon from 'src/images/icons/delete-icon.svg';

import css from './branch-view.css';


export default function BranchView({
  dispatch,
  node,
  edge,
  style,
  usedConditions,
  answerIndices,
  disabled,
  onSize,
  edgeError,
}: {
  dispatch: ConversationDispatch;
  node: QuestionNode | LATNode | TextToApplyNode | SchedulerNode;
  edge: ConditionEdge;
  style: Record<any, any>;
  usedConditions: Set<AnswerId>;
  answerIndices: Map<AnswerId, number>;
  disabled?: unknown;
  onSize: (arg1: ConditionEdge, arg2: number) => unknown;
  edgeError: Validation | null | undefined;
}) {
  const {t} = useLabelTranslation();
  const isSms = useSmsContext();

  const answersMap: Map<ConditionValue, Answer> = keyBy(
    node.answers ? node.answers : [],
    'id',
  );

  const {conditions} = edge;

  // NOTE (kyle): for the default branch we show unused answers. this means
  // that we have to recalc the height any time they change.
  const unusedAnswers = !conditions
    ? (node.answers || []).filter((answer) => !usedConditions.has(answer.id))
    : [];
  const unusedAnswerLabels = unusedAnswers
    .map((answer) => answer.label)
    .join('::');

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const ref = React.useRef();
  React.useLayoutEffect(() => {
    // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
    onSize(edge, ref.current?.offsetHeight || 0);
  }, [edge, unusedAnswerLabels, node]);

  // NOTE (kyle): cannot add to the default edge
  const canAddMore = conditions && usedConditions.size < answersMap.size + 1;

  // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'LATNode | QuestionNode | SchedulerNode | TextToApplyNode'.
  const showOperator = node.question_is_multiselect && edge.conditions;

  // NOTE (kyle): the "default" edge has no conditions and is not editable.
  return (
    <div
      className={classify(
        css.box,
        edgeError && css.error,
        showOperator && css.multiSelect,
      )}
      style={style}
      // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
      ref={ref}
    >
      {showOperator && (
        <div className={css.multiSelectOperator}>
          {getConditionOperatorLabels(t)[edge.operator || 'every']}
        </div>
      )}

      {!conditions ? (
        (() => {
          const unusedEnabledAnswers = unusedAnswers.filter(
            (answer) => !answer.disabled,
          );
          // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'LATNode | QuestionNode | SchedulerNode | TextToApplyNode'.
          return !node.question_is_multiselect &&
            unusedEnabledAnswers.length > 0 ? (
            <div className={css.options}>
              {unusedEnabledAnswers.slice(0, 5).map((answer) => (
                <div className={css.disabledCondition} key={answer.id}>
                  <ConditionLabel
                    answerIndices={answerIndices}
                    answer={answer}
                    isSms={isSms}
                  />
                </div>
              ))}
              {unusedEnabledAnswers.length > 5 && (
                <div className={css.conditionText}>
                  {`+${formatCompactNumber(
                    unusedEnabledAnswers.length - 5,
                  )} ${t('more', 'more')}`}
                </div>
              )}
            </div>
          ) : (
            <div className={css.defaultText}>
              {t('Any_other_response', 'Any other response')}
            </div>
          );
        })()
      ) : (
        <>
          <div className={css.options}>
            {conditions.length === 0 ? (
              <div className={css.noConditions}>
                {t('No_conditions', 'No conditions')}
              </div>
            ) : (
              conditions.map((answerId) => (
                <div
                  className={disabled ? css.disabledCondition : css.condition}
                  key={String(answerId)}
                  // @ts-expect-error - TS2322 - Type '(() => void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
                  onClick={
                    !disabled
                      ? () =>
                          dispatch({
                            type: 'REMOVE_CONDITION',
                            payload: {
                              edgeId: edge.id,
                              answerId,
                            },
                          })
                      : null
                  }
                >
                  <ConditionLabel
                    answerIndices={answerIndices}
                    answer={answersMap.get(answerId)}
                    isSms={isSms}
                  />
                  <span className={css.conditionRemove}>
                    {t('Remove', 'Remove')}
                  </span>
                </div>
              ))
            )}

            {!disabled && canAddMore && (
              <div
                className={css.addButton}
                onClick={() => dispatch({type: 'EDIT_EDGE', payload: edge.id})}
              >
                {`+ ${t('Add', 'Add')}`}
              </div>
            )}
          </div>

          {!disabled && (
            <UnstyledButton
              className={css.delete}
              onClick={() =>
                dispatch({
                  type: 'REMOVE_BRANCH',
                  payload: {
                    edgeId: edge.id,
                  },
                })
              }
            >
              <DeleteIcon />
            </UnstyledButton>
          )}
        </>
      )}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'answerIndices' implicitly has an 'any' type. | TS7031 - Binding element 'answer' implicitly has an 'any' type. | TS7031 - Binding element 'isSms' implicitly has an 'any' type.
function ConditionLabel({answerIndices, answer, isSms}) {
  let label;
  if (isSms && answer) {
    const index = answerIndices.get(answer.id);
    label = index != null ? getMultipleChoiceLabel(index) : null;
  }
  return (
    <span className={css.conditionLabel}>
      {label && `${label}: `}
      {answer?.label}
    </span>
  );
}

function ConditionSelector({
  node,
  onSelect,
  usedConditions,
}: {
  node: QuestionNode;
  onSelect: (arg1: AnswerId) => unknown;
  usedConditions: Set<AnswerId>;
}) {
  const answers = node.answers || [];

  return (
    <div className={css.conditionSelector}>
      {answers
        .filter((answer) => !usedConditions.has(answer.id))
        .map((answer) => (
          <UnstyledButton
            className={css.condition}
            key={answer.id}
            onClick={() => onSelect(answer.id)}
          >
            {answer.label}
          </UnstyledButton>
        ))}
    </div>
  );
}
